import { ArrowLeftIcon, CopyIcon, TrashIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Outlet, useParams } from 'react-router-dom'

import { useDeleteAnnouncement } from '@/api/communication/announcements/delete-announcement'
import { useGetCommunicationDetails } from '@/api/communication/get-communication-details'
import { useGetCommunicationHistory } from '@/api/communication/get-communication-history'
import { useGetMailingList } from '@/api/mailing-list/get-mailing-list'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'
import { EMPTY_VALUE } from '@/constants'
import { popupFactory, routerFactory, toastFactory } from '@/factories'
import { CommunicationStatusEnum, CommunicationTypeEnum } from '@/models'
import { getChannelName } from '@/pages/communication/announcements/announcements.utils'
import { selectBuildingsForFilter } from '@/reducers/buildingSelectorReducer'
import { formatDateOnly, formatTimeOnly } from '@/utils/date'
import { getApiErrorMessage } from '@/utils/error'

import {
  fetchAttachmentFiles,
  getAnnouncementStatusColor,
  getRecipientsLabel,
} from './announcement-details.page.utils'
import { AnnouncementDetailsAnalytics } from './announcement-details-analytics'
import { AnnouncementDetailsPanel } from './announcement-details-panel'
import { DeleteAnnouncementConfirmationPopup } from './delete-announcement-confirmation-popup'
import { NewMessageFormModal } from './new-message-form-modal'

export const AnnouncementDetails = () => {
  const { t } = useTranslation(['communication'])
  const params = useParams()
  const { navigate } = routerFactory.useRouter()

  const announcementId = Number(params.id)

  const buildings = useSelector(selectBuildingsForFilter)

  const {
    data: rawDetails,
    isLoading: isLoadingDetails,
    refetch: refetchDetails,
  } = useGetCommunicationDetails({
    id: announcementId,
  })

  const selectedBuilding = useMemo(
    () => buildings.find((b) => b.id === rawDetails?.buildingId),
    [buildings, rawDetails?.buildingId],
  )

  const { data: mailingListData } = useGetMailingList({
    buildingId: rawDetails?.buildingId,
  })

  const mailingListOptions = useMemo(() => {
    if (!mailingListData) return []

    return mailingListData?.map((item) => ({ ...item, label: item.value }))
  }, [mailingListData])

  const { data: historyData, refetch: refetchHistory } = useGetCommunicationHistory({
    buildingId: selectedBuilding?.id,
    limit: 50,
    query: rawDetails?.title,
  })
  const communicationPortalHistory = historyData?.history ?? []

  const dataForEdit = useMemo(
    () => communicationPortalHistory.find((item) => item.id === +announcementId),
    [communicationPortalHistory, announcementId],
  )

  const panelData = useMemo(() => {
    if (!rawDetails) return null

    const timezone = selectedBuilding?.timezone || ''
    const isCompleted = rawDetails.status === CommunicationStatusEnum.enum.Completed
    const timestamp = rawDetails.scheduledDate ?? rawDetails.createdAt
    const zonedDate = toZonedTime(timestamp, timezone)
    const formattedDate = `${formatDateOnly(zonedDate)} ${formatTimeOnly(zonedDate)}`

    const mailingList =
      mailingListData?.find((item) => item.id === rawDetails.mailingList)?.value ?? EMPTY_VALUE
    const recipients = getRecipientsLabel(
      {
        building: selectedBuilding,
        recipientsCount: rawDetails.recipientsCount,
        standaloneClientIds: rawDetails.standaloneClientIds,
        type: rawDetails.type,
        userGroupIds:
          rawDetails.recipientTypes.userGroupIds ?? dataForEdit?.recipientTypes?.userGroupIds,
      },
      t,
    )

    return {
      attachments: dataForEdit?.attachments ?? [],
      body: rawDetails.message,
      buildingNumber: rawDetails.buildingNumber || undefined,
      floors: rawDetails.floor?.length ? rawDetails.floor.join(', ') : undefined,
      from: rawDetails.from,
      phase: rawDetails.yardiPropertyId || undefined,
      image: dataForEdit?.image ?? null,
      mailingList,
      recipients,
      replyTo: rawDetails.replyTo,
      subject: rawDetails.title,
      type: rawDetails.type,
      ...(isCompleted ? { timeSent: formattedDate } : { timeScheduled: formattedDate }),
    }
  }, [rawDetails, selectedBuilding, mailingListData, dataForEdit, t])

  const preparedTitle = rawDetails?.type
    ? t('communication:announcementDetails.title', { type: getChannelName(rawDetails.type, t) })
    : ''

  const { mutateAsync: deleteAnnouncement } = useDeleteAnnouncement()

  const commonFormProps = {
    fetchAnnouncementDetails: refetchDetails,
    mailingListOptions,
    refreshTable: refetchHistory,
    type: rawDetails?.type,
  }

  const standaloneClients = rawDetails?.recipientTypes?.clients ?? []

  const openEdit = () => {
    if (!dataForEdit || !selectedBuilding) return

    popupFactory.open(NewMessageFormModal, {
      ...commonFormProps,
      previewData: { ...dataForEdit, message: rawDetails?.message, standaloneClients },
      selectedBuilding,
    })
  }

  const openDuplicate = async () => {
    if (!selectedBuilding) return

    // Note: A notification sends its image as the first attachment, so the image is re-uploaded the same way
    const isNotification = rawDetails?.type === CommunicationTypeEnum.enum.Notification
    const filesToCopy = isNotification
      ? dataForEdit?.image
        ? [{ fileName: dataForEdit.image }]
        : []
      : (dataForEdit?.attachments ?? [])

    let attachments: File[] | undefined
    try {
      attachments = filesToCopy.length ? await fetchAttachmentFiles(filesToCopy) : undefined
    } catch {
      toastFactory.error(t('communication:announcementDetails.duplicateAttachmentsError'))
    }

    popupFactory.open(NewMessageFormModal, {
      ...commonFormProps,
      ...(dataForEdit && {
        previewData: {
          ...dataForEdit,
          attachments,
          id: undefined,
          ...(isNotification && { image: null }),
          message: rawDetails?.message,
          standaloneClients,
        },
      }),
      isDuplicateAnnouncementFormActive: true,
      selectedBuilding,
    })
  }

  const handleDelete = () => {
    if (!rawDetails) return
    popupFactory.open(DeleteAnnouncementConfirmationPopup, {
      announcementTitle: rawDetails.title,
      onConfirm: async () => {
        try {
          await deleteAnnouncement({ id: rawDetails.id, type: rawDetails.type })
          toastFactory.success(
            t('communication:announcementDetails.deleteSuccess', {
              type: getChannelName(rawDetails.type, t),
            }),
          )
          navigate({ path: '/dashboard/announcements' })
        } catch (error) {
          toastFactory.error(getApiErrorMessage(error))
        }
      },
    })
  }

  const isCompleted = rawDetails?.status === CommunicationStatusEnum.enum.Completed
  const showDelete = rawDetails?.type !== CommunicationTypeEnum.enum.SMS && !isCompleted
  const showEditDelete = rawDetails?.status === CommunicationStatusEnum.enum.Scheduled

  return (
    // TODO: Remove Dashboard.Container from here. It's added because announcements is outside of /building-dashboard routes. Padding and background color has to be provided to match rest of the pages.
    <div className="announcement-details">
      <Page>
        <PageHeader
          loading={isLoadingDetails}
          title={preparedTitle}
          backButton={
            <Link
              navigateBack
              use="secondary"
              size="small"
              to={{ path: '/dashboard/announcements' }}
            >
              <Icon icon={ArrowLeftIcon} />
              {t('communication:announcementDetails.back')}
            </Link>
          }
          badges={
            rawDetails?.status && (
              <Badge
                color={getAnnouncementStatusColor(rawDetails?.status)}
                size="small"
                variant="light"
              >
                {rawDetails?.status}
              </Badge>
            )
          }
          actions={
            <>
              {showEditDelete && (
                <Button use="secondary" size="small" onClick={openEdit}>
                  {t('communication:announcementDetails.actions.edit')}
                </Button>
              )}
              <Button use="secondary" size="small" onClick={openDuplicate}>
                <Icon icon={CopyIcon} />
                {t('communication:announcementDetails.actions.duplicate')}
              </Button>
              {showDelete && (
                <Button use="secondary" size="small" onClick={handleDelete}>
                  <Icon icon={TrashIcon} />
                  {t('communication:announcementDetails.actions.delete')}
                </Button>
              )}
            </>
          }
        />
        {panelData && <AnnouncementDetailsPanel data={panelData} />}
        {isCompleted && (
          <AnnouncementDetailsAnalytics id={rawDetails?.id} announcementType={rawDetails?.type} />
        )}
        {isCompleted && <Outlet />}
      </Page>
    </div>
  )
}
